import { Identity } from '@sdbn/common';
import { ServiceResponse } from '@sdbn/common';
import { Station } from '../../../../client/model/station';
import { StationGroup } from '../../../../client/model/station-group';
import { StationSet } from '../../../../client/model/station-set';
import { RoutingOptionType } from '../../../../client/model/station-usage.model';
import { Transport, TransportRepresentation, TransportScope } from '../../../../common/model/transport.model';

export class SacStructure {

  readonly stationGroup: StationGroup;
  readonly stationSet: StationSet;
  readonly title: string;

  constructor(structure?: any) {
    if (structure != null) {
      this.title = structure.title;
      if (structure.stationGroup != null) {
        this.stationGroup = structure.stationGroup;
      }
      if (structure.stationSet != null) {
        this.stationSet = structure.stationSet;
      }
    }
  }

}

export class SacSelection {

  readonly stationSet: StationSet;
  readonly station: Station;

  constructor(selection?: any) {
    if (selection != null) {
      this.stationSet = selection.stationSet;
      this.station = selection.station;
    }
  }

}

export class CancellationStation {

  id: number;
  title: string;

  constructor(station: any) {
    this.id = station.id;
    this.title = station.title;
  }
}

export class TransportGroup {

  key: string;
  nameDictionaryKey: string;
  transports = new Array<AvailableTransport>();

  constructor(group: any) {
    this.key = group.key;
    this.nameDictionaryKey = group.nameDictionaryKey;
  }

}

export class AvailableTransports {
  transports = new Array<AvailableTransport>();
  backupTransports = new Array<AvailableTransport>();

  constructor(at: AvailableTransports) {
    if (at.transports != null) {
      for (const t of at.transports) {
        this.transports.push(new AvailableTransport(t));
      }
      for (const t of at.backupTransports) {
        this.backupTransports.push(new AvailableTransport(t));
      }
    }
  }
}

export class AvailableTransport implements TransportRepresentation {

  id: number;
  currentStationId: number;
  umbrueckungTargetTransportId: number;
  icons = new Array<string>();

  messageKeys = new Array<string>();
  createMovementOrderAllowed: boolean;
  assignAllowed: boolean;
  assignEnabled: boolean;
  cancelAllowed: boolean;
  startUmbrueckungAllowed: boolean;
  startUmbrueckungEnabled: boolean;
  completeUmbrueckungAllowed: boolean;
  completeUmbrueckungEnabled: boolean;
  asBackup: boolean;
  plannedForAssignment: boolean;
  automaticallyPlanned: boolean;

  transport = new Transport();

  public get name(): string {
    return this.transport?.transportName;
  }

  public get scope(): TransportScope {
    return this.transport?.scope;
  }

  constructor(transport: any) {
    this.update(transport);
  }

  public update(transport: AvailableTransport) {
    this.id = transport?.id;
    this.currentStationId = transport?.currentStationId;
    this.umbrueckungTargetTransportId = transport?.umbrueckungTargetTransportId;
    this.createMovementOrderAllowed = transport?.createMovementOrderAllowed;

    this.icons = new Array<string>();
    if (transport.icons != null) {
      this.icons.push(...transport.icons);
    }

    this.messageKeys = new Array<string>();
    if (transport.messageKeys != null) {
      this.messageKeys.push(...transport.messageKeys);
    }

    this.assignAllowed = transport?.assignAllowed;
    this.assignEnabled = transport?.assignEnabled;
    this.cancelAllowed = transport?.cancelAllowed;
    this.startUmbrueckungAllowed = transport?.startUmbrueckungAllowed;
    this.startUmbrueckungEnabled = transport?.startUmbrueckungEnabled;
    this.completeUmbrueckungAllowed = transport?.completeUmbrueckungAllowed;
    this.completeUmbrueckungEnabled = transport?.completeUmbrueckungEnabled;
    this.asBackup = transport?.asBackup;
    this.plannedForAssignment = transport?.plannedForAssignment;
    this.automaticallyPlanned = transport?.automaticallyPlanned;
    this.transport.update(transport?.transport);
  }
}

export class AvailableTransportsSelection {
  stationSet: Identity;
  station: Identity;
}

export class VehicleSelection {
  query: string;
}

export class Vehicle {
  id: number;
  carrier: string;
  identifier: string;
  driverName: string;
  currentStation: string;

  constructor(vehicle?: any) {
    if (vehicle != null) {
      this.id = vehicle.id;
      this.carrier = vehicle.carrier;
      this.identifier = vehicle.identifier;
      this.driverName = vehicle.driverName;
      this.currentStation = vehicle.currentStation;
    }
  }
}

export class TransportSelection {
  dateFrom: Date;
  dateUntil: Date;
  query: string;
  targetStationId: number;
  onlyPlanned: boolean;
  findOnlyNextPlannedRoutingPosition: boolean;

  constructor(selection?: any) {
    if (selection != null) {
      this.dateFrom = selection.dateFrom;
      this.dateUntil = selection.dateUntil;
      this.query = selection.query;
      this.targetStationId = selection.targetStationId;
      this.onlyPlanned = selection.onlyPlanned;
    }
  }
}

export class PlannedTransport {

  id: number;
  carrier: string;
  supplier: string;
  identifier: string;
  driver: string;
  name: string;
  visitStatusDict: string;
  string1: string;
  string2: string;
  start: string;
  status: string;
  reference: string;
  options: string;
  cargo: string;
  planningDate: string;
  messagingResources: string;

  constructor(transport?: any) {
    if (transport != null) {
      this.id = transport.id;
      this.carrier = transport.carrier;
      this.supplier = transport.supplier;
      this.identifier = transport.identifier;
      this.driver = transport.driver;
      this.name = transport.name;
      this.visitStatusDict = transport.visitStatusDict;
      this.status = transport.status;
      this.string1 = transport.string1;
      this.string2 = transport.string2;
      this.start = transport.start;
      this.reference = transport.reference;
      this.options = transport.options;
      this.cargo = transport.cargo;
      this.planningDate = transport.planningDate;
      this.messagingResources = transport.messagingResources;
    }
  }

}

export class AssignTransportToStation {
  transportStationAssignment: TransportStationAssignment;
  loadingZoneId: number;
  asBackup: boolean;
  workingStationSet: Identity;
}

export class CreatePlannedAssignment {
  plannedTransport: Identity;
  targetStation: Identity;
  targetRoutingPosition: Identity;
}

export class PlannedTransportAssignment {

  transportAssignmentId: number;
  transport: Transport;

  constructor(assignment: any) {
    this.transportAssignmentId = assignment?.transportAssignmentId;
    this.transport = new Transport(assignment?.transport);
  }
}

export class CancelPlannedAssignment {
  transportAssignment: Identity;
}

export class RoutingPositionOption {
  routingPositionId: number;
  label: string;
}

export class RoutingPositionOptionSelection {
  targetStation: Identity;
  transport: Identity;
}

export class BeginUmbrueckung {

  sourceTransport: Identity;
  sourceTransportStationId: Identity;
  targetTransport: Identity;
  targetTransportStationId: Identity;
  trailerNumberPlate: string;
  swapTrailerNumber: string;

}

export class UmbrueckungDetails extends ServiceResponse {

  id: number;
  carrier: string;
  name: string;
  identifier: string;
  transportScope: string;
  currentStationId: number;
  swapTrailerNumber: string;
  trailerNumberPlate: string;
  targetTransportScope: string;

  constructor(details: Partial<UmbrueckungDetails>) {
    super(details);
    this.id = details.id;
    this.carrier = details.carrier;
    this.name = details.name;
    this.identifier = details.identifier;
    this.transportScope = details.transportScope;
    this.currentStationId = details.currentStationId;
    this.swapTrailerNumber = details.swapTrailerNumber;
    this.trailerNumberPlate = details.trailerNumberPlate;
    this.targetTransportScope = details.targetTransportScope;
  }
}

export class StructureSelection {
  stationGroupId: number;
  stationSetId: number;
}
export class CancelTransport {
  transportStationAssignment: TransportStationAssignment;
  targetStationForRepeat: Identity;
  canellationReason: string;
  targetStationSetForForwarding: Identity;
  targetStationForForwarding: Identity;
  forwardingOptionDictionaryKey: string;
}

export class SetProcessingStatus {
  transportStationAssignment: TransportStationAssignment;
  oldProcessingStatusModelValue: string;
  processingStatusModelValue: string;
}

export class TransportStationAssignment {
  transport: Identity;
  station: Identity;
}

export class SendTransportToStation {
  transportStationAssignment: TransportStationAssignment;
  targetStationSet: Identity;
  targetStation: Identity;
  routingOptionType: RoutingOptionType;
  transportOptionIds: Array<number>;
  forwardingOptionDictionaryKey: string;
}

export class AssignmentServiceResponse extends ServiceResponse {
  suggestedTransportId: number;
  suggestTransportDescription: string;
  constructor(response: any) {
    super(response);
    this.suggestedTransportId = response.suggestedTransportId;
    this.suggestTransportDescription = response.suggestTransportDescription;
  }
}

export class SuspendStation {
  stationId: number;
  note: string;
}

export class MovementOrderRequest {

  transportId: number;
  vehicleId: number;
  targetStationId: number;
  currentStationId: number;

  constructor(request?: any) {
    if (request != null) {
      this.transportId = request.transportId;
      this.targetStationId = request.targetStationId;
      this.currentStationId = request.currentStationId;
      this.vehicleId = request.vehicleId;
    }
  }

}

export class ForwardToProcess {

  asVehicle: boolean;
  selectedOptionIds = new Array<number>();

  constructor(forward?: Partial<ForwardToProcess>) {
    if (forward != null) {
      this.asVehicle = forward.asVehicle;
      this.selectedOptionIds = forward.selectedOptionIds;
    }
  }

}

export class DisableStationFetch {
  disableForMinutes: number;
  stationId: number;

  constructor(disableStationFetch: Partial<DisableStationFetch>) {
    this.disableForMinutes = disableStationFetch?.disableForMinutes;
    this.stationId = disableStationFetch?.stationId;
  }
}
